import React, { useEffect, useRef } from 'react';
import { encodeMatrix } from './qrcode';

/**
 * QrCanvas — renders a QR code for `value` onto a <canvas>, dependency-free.
 * Mobile-first: scales to the container width via the `size` prop (px).
 * Fail-safe: if the payload is too large to encode, renders a readable notice
 * instead of throwing.
 */
export default function QrCanvas({ value, size = 260, quietZone = 4 }) {
  const canvasRef = useRef(null);
  const failedRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    let matrix = null;
    try {
      matrix = encodeMatrix(value);
    } catch (e) {
      matrix = null;
    }

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // White card behind the code so dark-themed pages still scan cleanly.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    if (!matrix) {
      failedRef.current = true;
      ctx.fillStyle = '#e74d3c';
      ctx.font = '13px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('QR payload too large', size / 2, size / 2);
      return;
    }
    failedRef.current = false;

    const count = matrix.length;
    const totalModules = count + quietZone * 2;
    const moduleSize = size / totalModules;

    ctx.fillStyle = '#0b0b0f';
    for (let r = 0; r < count; r += 1) {
      for (let c = 0; c < count; c += 1) {
        if (matrix[r][c]) {
          ctx.fillRect(
            Math.floor((c + quietZone) * moduleSize),
            Math.floor((r + quietZone) * moduleSize),
            Math.ceil(moduleSize),
            Math.ceil(moduleSize),
          );
        }
      }
    }
  }, [value, size, quietZone]);

  return (
    <canvas
      ref={canvasRef}
      aria-label="QR code"
      style={{ borderRadius: 12, display: 'block' }}
    />
  );
}
